import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import Form from "../components/form/Form";
import FormControl from "../components/form/FormControl";
import Input from "../components/form/Input";
import GenericTable, { GenericTableColumn } from "../components/GenericTable";
import Modal from "../components/Modal";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import { readErrorMessage } from "../lib/http";
import { AppUser, LoginMethod, UserRole, UserStatus } from "../types/auth";

const roleOptions: Array<{ value: UserRole; label: string }> = [
  { value: "secretary", label: "Secretary" },
  { value: "manager", label: "Manager" },
  { value: "admin", label: "Admin" },
];

const statusLabels: Record<UserStatus, string> = {
  invited: "Invited",
  active: "Active",
  disabled: "Disabled",
};

const statusBadgeClassName: Record<UserStatus, string> = {
  invited: "statusBadge statusBadgeUpcoming",
  active: "statusBadge statusBadgeOngoing",
  disabled: "statusBadge statusBadgeEnded",
};

const loginMethodLabels: Record<LoginMethod, string> = {
  google: "Google",
  password: "Email & password",
};

type InviteFormValues = {
  email: string;
  fullName: string;
  role: UserRole;
  loginMethod: LoginMethod;
};

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("is-IS") : "—";

function UsersPage() {
  const { authorizedFetch, canManageUsers, me } = useAppSession();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const inviteForm = useForm<InviteFormValues>({
    defaultValues: {
      email: "",
      fullName: "",
      role: "secretary",
      loginMethod: "google",
    },
  });
  const inviteLoginMethod = inviteForm.watch("loginMethod");

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const response = await authorizedFetch("/users");
      if (!response.ok) {
        throw new Error(
          await readErrorMessage(
            response,
            `Failed to load users (${response.status}).`,
          ),
        );
      }
      setUsers((await response.json()) as AppUser[]);
    } catch (error) {
      setLoadError(
        error instanceof Error ? error.message : "Failed to load users.",
      );
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [authorizedFetch]);

  useEffect(() => {
    if (canManageUsers) {
      void loadUsers();
    }
  }, [canManageUsers, loadUsers]);

  const sendJson = async (path: string, method: string, body?: unknown) => {
    const response = await authorizedFetch(path, {
      method,
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(
        await readErrorMessage(
          response,
          `Request failed (${response.status}).`,
        ),
      );
    }
    return response.status === 204 ? null : response.json();
  };

  const runUserAction = async (
    userId: string,
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    setBusyUserId(userId);
    try {
      await action();
      toast.success(successMessage);
      await loadUsers();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Request failed.");
    } finally {
      setBusyUserId(null);
    }
  };

  const onInvite = async (values: InviteFormValues) => {
    try {
      const created = (await sendJson("/users", "POST", {
        email: values.email,
        fullName: values.fullName || undefined,
        role: values.role,
        loginMethod: values.loginMethod,
      })) as { inviteEmailSent: boolean };
      toast.success(
        values.loginMethod === "google"
          ? "User invited. They can now sign in with Google."
          : created.inviteEmailSent
            ? "Invitation sent."
            : "User added. They already have an account and can log in.",
      );
      setIsInviteOpen(false);
      inviteForm.reset();
      await loadUsers();
    } catch (error) {
      inviteForm.setError("root", {
        message: error instanceof Error ? error.message : "Invite failed.",
      });
    }
  };

  if (!canManageUsers) {
    return (
      <PageSectionCard titleId="users-title" eyebrow="Users" title="Users">
        <p className="hintText">Only admins can manage users.</p>
      </PageSectionCard>
    );
  }

  const columns: GenericTableColumn<AppUser>[] = [
    {
      id: "email",
      header: "Email",
      cell: (user) => (
        <>
          <div>{user.email}</div>
          {user.fullName ? (
            <div className="hintText">{user.fullName}</div>
          ) : null}
        </>
      ),
    },
    {
      id: "role",
      header: "Role",
      cell: (user) => (
        <select
          className="inputControl"
          aria-label={`Role for ${user.email}`}
          value={user.role}
          disabled={user.id === me?.userId || busyUserId === user.id}
          onChange={(event) => {
            const role = event.target.value as UserRole;
            void runUserAction(
              user.id,
              () => sendJson(`/users/${user.id}`, "PATCH", { role }),
              "Role updated.",
            );
          }}
        >
          {roleOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ),
    },
    {
      id: "status",
      header: "Status",
      cell: (user) => (
        <span className={statusBadgeClassName[user.status]}>
          {statusLabels[user.status]}
        </span>
      ),
    },
    {
      id: "loginMethod",
      header: "Sign-in",
      cell: (user) => loginMethodLabels[user.loginMethod],
    },
    {
      id: "lastLoginAt",
      header: "Last login",
      cell: (user) => formatDate(user.lastLoginAt),
    },
    {
      id: "actions",
      header: "Actions",
      headerClassName: "actionsColumn",
      cellClassName: "actionsColumn",
      cell: (user) => {
        if (user.id === me?.userId) {
          return <span className="hintText">You</span>;
        }
        const busy = busyUserId === user.id;

        return (
          <div className="headerActions">
            {user.status === "invited" && user.loginMethod === "password" ? (
              <button
                className="secondaryButton tableActionButton"
                type="button"
                disabled={busy}
                onClick={() =>
                  void runUserAction(
                    user.id,
                    () => sendJson(`/users/${user.id}/resend-invite`, "POST"),
                    "Invitation re-sent.",
                  )
                }
              >
                Resend invite
              </button>
            ) : null}

            {user.status === "invited" && !user.lastLoginAt ? (
              <button
                className="secondaryButton tableActionButton"
                type="button"
                disabled={busy}
                onClick={() =>
                  void runUserAction(
                    user.id,
                    () => sendJson(`/users/${user.id}`, "DELETE"),
                    "Invite cancelled.",
                  )
                }
              >
                Cancel invite
              </button>
            ) : null}

            <button
              className="secondaryButton tableActionButton"
              type="button"
              disabled={busy}
              onClick={() => {
                const status =
                  user.status === "disabled" ? "active" : "disabled";
                void runUserAction(
                  user.id,
                  () => sendJson(`/users/${user.id}`, "PATCH", { status }),
                  status === "disabled" ? "User disabled." : "User enabled.",
                );
              }}
            >
              {user.status === "disabled" ? "Enable" : "Disable"}
            </button>
          </div>
        );
      },
    },
  ];

  const rootError = inviteForm.formState.errors.root?.message;

  return (
    <PageSectionCard
      titleId="users-title"
      eyebrow="Users"
      title="Users"
      lead="Login is by invitation only. Invite staff and choose their role."
      actions={
        <div className="headerActions">
          <button
            className="primaryButton"
            type="button"
            onClick={() => setIsInviteOpen(true)}
          >
            Invite user
          </button>
        </div>
      }
    >
      {isLoading ? <p>Loading users...</p> : null}

      {loadError ? (
        <p className="errorMessage" role="alert">
          {loadError}
        </p>
      ) : null}

      {!isLoading && !loadError ? (
        <GenericTable
          ariaLabel="Users"
          columns={columns}
          rows={users}
          rowKey={(user) => user.id}
        />
      ) : null}

      <Modal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        titleId="invite-user-title"
        title="Invite user"
        description={
          inviteLoginMethod === "google"
            ? "They sign in with Google using this email address. No email is sent; let them know they have access."
            : "They will receive an email to set their password."
        }
        footer={
          <>
            <button
              className="secondaryButton"
              type="button"
              onClick={() => setIsInviteOpen(false)}
            >
              Cancel
            </button>
            <button
              className="primaryButton"
              type="submit"
              form="invite-user-form"
              disabled={inviteForm.formState.isSubmitting}
            >
              {inviteForm.formState.isSubmitting ? "Sending..." : "Send invite"}
            </button>
          </>
        }
      >
        <Form form={inviteForm} onSubmit={onInvite} id="invite-user-form">
          <FormControl<InviteFormValues> name="email" label="Email">
            <Input<InviteFormValues>
              name="email"
              type="email"
              autoComplete="off"
              rules={{ required: "Email is required" }}
            />
          </FormControl>

          <FormControl<InviteFormValues> name="fullName" label="Full name">
            <Input<InviteFormValues> name="fullName" autoComplete="off" />
          </FormControl>

          <FormControl<InviteFormValues> name="role" label="Role">
            <select className="inputControl" {...inviteForm.register("role")}>
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </FormControl>

          <FormControl<InviteFormValues> name="loginMethod" label="Sign-in">
            <select
              className="inputControl"
              {...inviteForm.register("loginMethod")}
            >
              {(Object.keys(loginMethodLabels) as LoginMethod[]).map(
                (method) => (
                  <option key={method} value={method}>
                    {loginMethodLabels[method]}
                  </option>
                ),
              )}
            </select>
          </FormControl>

          {rootError ? (
            <p className="errorMessage" role="alert">
              {rootError}
            </p>
          ) : null}
        </Form>
      </Modal>
    </PageSectionCard>
  );
}

export default UsersPage;
